import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React, { useEffect, useState } from "react";

import { paymentTerms } from "../../Enums/enumValues";
import { useDlpFlowContext } from "../../hooks/useDlpContext";
import { Enum } from "../../interfaces/enum";
import { FlowSidebar } from "../../layouts/FlowSidebar";
import useIsClient from "../../lib/useIsClient";
import { Button, Props as ButtonProps } from "../buttons/Button";
import { currencyFormat } from "../InputFields/sanitizer/currencyFormat";
import { VerticalRadioInput } from "../InputFields/VerticalRadioInput";
import styles from "./DlpSummary.module.scss";
import { DlpSummaryCoverage } from "./DlpSummaryCoverage";

interface Props {
  cannotEdit?: boolean;
  submitButtonProps: ButtonProps;
  fields: Record<string, Field<string>>;
}

export const DlpSummary = ({ cannotEdit, submitButtonProps, fields }: Props) => {
  const { data, premium, isLoadingPremium, setters, dictionary } = useDlpFlowContext();
  const { isClient } = useIsClient();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const nrOfCoverages = data.coverages.length;

    if (nrOfCoverages !== 0 && nrOfCoverages - 1 < index) {
      setIndex(nrOfCoverages - 1);
    }
  }, [data.coverages, index]);

  const terms = premium?.totalPremiumPerTerms || [];
  const premiumTermsOptions = terms.map((term) => {
    const option = (paymentTerms() as Enum[]).filter((x) => x.value === term.paymentTerm);
    return {
      value: term.paymentTerm,
      disabled: false,
      label: option.length ? option[0].label : "",
    };
  });

  const selectedPremiumValue = terms.filter((t) => t.paymentTerm === data.paymentTerm);
  const selectedPremium =
    data.paymentTerm && selectedPremiumValue.length > 0 ? currencyFormat(selectedPremiumValue[0].premium) : "";

  const hasCoverages = (() => {
    const coverages = data.coverages;
    const firstInsured = data.insuredPersons[0];
    const hasFirstInsured =
      Boolean(firstInsured.dateOfBirth) && Boolean(firstInsured.firstName) && Boolean(firstInsured.isSmoking);
    const hasNotEmptyCoverages = coverages ? coverages.filter((c) => c.effectiveInsured?.length !== 0) : [];

    return hasFirstInsured && hasNotEmptyCoverages.length > 0;
  })();

  const hasCompleteCoverages = (() => {
    const coverages = data.coverages;
    const hasNotEmptyAnnuityCoverages = coverages
      ? coverages.filter((c) => (c.insuranceType === "Annuity" && c.reductionRate) || c.insuranceType !== "Annuity")
      : [];
    return hasNotEmptyAnnuityCoverages.length > 0;
  })();

  const titleId = "summary-title";
  const paymentTermId = "summary-paymentterm";
  const paymentTermHeadingId = `${paymentTermId}-heading`;

  const hasPremium = terms.length > 0;

  const showSummaryPrices = hasCoverages && hasCompleteCoverages && hasPremium && isClient;

  const showCoverages = hasCoverages && isClient;

  return (
    <>
      <FlowSidebar
        title={
          <>
            <Text field={fields.Title} />
            {isLoadingPremium && <div className="loadingspinner" title="Loading" />}
          </>
        }
        titleId={titleId}
      >
        <>
          {showSummaryPrices && (
            <div className={styles.dlpSummary__summaryPrices}>
              <div>
                <p id={paymentTermHeadingId}>
                  <strong>
                    <Text field={fields.ChoosePaymentTermLabel}></Text>
                  </strong>
                </p>
                <VerticalRadioInput
                  ariaLabelledBy={paymentTermHeadingId}
                  wrapOptionInDiv="No"
                  radioRowClassName="form-item full-width radio"
                  selectedRadioItem={data.paymentTerm}
                  onChange={setters.updatePaymentTerm}
                  disabled={cannotEdit}
                  name="PaymentTerm"
                  options={premiumTermsOptions}
                />
              </div>
              <div className={styles.dlpSummary__summaryPrice}>{selectedPremium}</div>
            </div>
          )}
          <div className={styles.dlpSummary__summaryContent}>
            {!hasCoverages && (
              <p>
                <Text field={fields.NoDataLabel}></Text>
              </p>
            )}

            {showCoverages && (
              <>
                {data.coverages.length > 1 && (
                  <div className={styles.dlpSummary__coverageButtonsWrapper}>
                    {data.coverages.map((_, coIndex: number) => (
                      <Button
                        key={`coverage_button_${coIndex + 1}`}
                        id={`coverage_button_${coIndex + 1}`}
                        onClick={() => setIndex(coIndex)}
                        type={index === coIndex ? undefined : "link"}
                      >
                        <Text field={fields.Coverage}></Text> {coIndex + 1}
                      </Button>
                    ))}
                  </div>
                )}

                <div className={styles.dlpSummary__coverageWrapper}>
                  {data.coverages.map((coverage, coIndex: number) => {
                    const coveragePremium = premium?.premiumPerCoverage?.find(
                      (ppc) => ppc.coverageId === coverage.coverageId
                    );

                    const coveragePremiumPerPaymentTerm = coveragePremium?.premiumsPerTerms.find(
                      (t) => t.paymentTerm === data.paymentTerm
                    );

                    return (
                      <div
                        key={`dlpSummaryCoverage${coIndex}`}
                        className={styles.dlpSummary__coverageItem}
                        style={{
                          transform: `translateX(calc(${(coIndex - index) * 100}% + ${(coIndex - index) * 26}px))`,
                          opacity: coIndex - index === 0 ? 1 : 0.4,
                        }}
                      >
                        <DlpSummaryCoverage
                          index={coIndex}
                          coverage={coverage}
                          data={data}
                          fields={fields}
                          premiumPerPaymentTerm={coveragePremiumPerPaymentTerm}
                          dictionary={dictionary}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      </FlowSidebar>
      <div className={styles["dlpSummary__submit-button-wrapper"]}>
        <Button
          {...submitButtonProps}
          leading={<i className={`icon icon-pencil ${styles["dlpSummary__submit-button-icon"]}`} />}
          type="primary"
        />
      </div>
    </>
  );
};
